import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useSelector } from "react-redux";

import { getUser } from "../../../../pages/userSlice";
import {
  CreateUsers,
  GetActiveProduct,
  GetRolesByProduct,
} from "../../../../services/admin/adminServices";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CODES } from "../../../../consts/codes";
import ModalInfo from "../../Modals/modalInfo";
import PermissionsRegisters from "./permissionsRegistersV2";
import { REGEXP } from "../../../../consts/regexp";
import { AvailableCompaniesPermissions } from "../../../../services/user/consultRolesService";
import { UserInfoComponent } from "../../AdminUsersClient/userInfoComponent";
import { UserNRPList } from "./userNRPList";

const CreateUser = ({
  currentCompany,
  role,
  companyId,
  country,
  fetchUsers,
}) => {
  /**
   * Title Container component
   */
  const DialogTitleContainer = (props) => {
    const { children, onClose, ...other } = props;

    return (
      <DialogTitle {...other}>
        {children}
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
    );
  };

  /**
   * useTranslation
   */
  const { t } = useTranslation();

  /**
   * Schema
   */
  const schema = yup.object().shape({
    name: yup
      .string()
      .max(20, t("App.validationMessages.maxCharacters", { number: 20 }))
      .required(
        t("App.validationMessages.requiredV2", {
          complement: t("App.infonavitUsers.createForm.nameRequired"),
        })
      )
      .matches(REGEXP.ONLY_LETTERS, t("App.validationMessages.onlyString")),
    lastNames: yup
      .string()
      .max(50, t("App.validationMessages.maxCharacters", { number: 50 }))
      .required(
        t("App.validationMessages.requiredV2", {
          complement: t("App.infonavitUsers.createForm.lastnameRequired"),
        })
      )
      .matches(REGEXP.ONLY_LETTERS, t("App.validationMessages.onlyString")),
    email: yup
      .string()
      .max(50, t("App.validationMessages.maxCharacters", { number: 50 }))
      .email(
        t("App.validationMessages.requiredV2", {
          complement: t("App.infonavitUsers.createForm.invalidEmail"),
        })
      )
      .required(
        t("App.validationMessages.requiredV2", {
          complement: t("App.infonavitUsers.createForm.emailRequired"),
        })
      ),
    confirmEmail: yup
      .string()
      .max(50, t("App.validationMessages.maxCharacters", { number: 50 }))
      .email(
        t("App.validationMessages.requiredV2", {
          complement: t("App.infonavitUsers.createForm.invalidEmail"),
        })
      )
      .oneOf(
        [yup.ref("email"), null],
        t("App.infonavitUsers.createForm.invalidConfirmEmail")
      )
      .required(
        t("App.validationMessages.requiredV2", {
          complement: t("App.infonavitUsers.createForm.confirmEmailRequired"),
        })
      ),
    phone: yup
      .string()
      .required(
        t("App.validationMessages.requiredV2", {
          complement: t("App.infonavitUsers.createForm.phoneRequired"),
        })
      )
      .matches(
        REGEXP.ONLY_NUMBERS,
        t("App.infonavitUsers.createForm.invalidPhone")
      )
      .min(2, t("App.validationMessages.minCharacters", { number: 2 }))
      .max(10, t("App.validationMessages.maxCharacters", { number: 10 }))
      .nullable(),

    permissions: yup.array().of(
      yup.object().shape({
        roleList: yup
          .string()
          .required(
            t("App.validationMessages.requiredV2", {
              complement: t("App.infonavitUsers.createForm.roleRequired"),
            })
          )
          .typeError(t("App.validationMessages.required")),
        status: yup.string().required(
          t("App.validationMessages.requiredV2", {
            complement: t("App.infonavitUsers.createForm.stateRequired"),
          })
        ),
      })
    ),
  });

  /**
   * useForm
   */
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors },
    formState,
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      permissions: [],
      name: "",
      lastNames: "",
      email: "",
      confirmEmail: "",
      phone: null,
      nrps: [],
    },
  });

  /**
   * useFieldArray que maneja el arreglo de permisos
   */
  const {
    fields: permissionsFields,
    append: permissionsAppend,
    remove: permissionsRemove,
    update: permissionsUpdate,
  } = useFieldArray({
    control,
    name: "permissions",
  });

  /**
   * Use State
   */
  const userInfo = useSelector(getUser);

  const [openCreateUser, setOpenCreateUser] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [isDesktop, setDesktop] = useState(window.innerWidth > 576);

  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [createUserData, setCreateUserData] = useState({});

  const [productsList, setProductsList] = useState([]);
  const [rolesMap, setRolesMap] = useState(new Map());
  const [companiesMap, setCompaniesMap] = useState(new Map());
  const [loadingProductsAndRoles, setLoadingProductsAndRoles] = useState(false);

  const [isBusinessGroup, setIsBusinessGroup] = useState(false);

  /**
   * useEffect para actualizar el tamaño de pantalla actual de la pantalla
   */
  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  });

  /**
   * Deja la primera lista de empresas limpia en caso de que sea BG,
   * dado que en el primer append no se alcanza a determinar si es BG o no
   */
  useEffect(() => {
    if (isBusinessGroup) {
      // setValue("permissions.0.companyList", []);
    }
  }, [isBusinessGroup]);

  /**
   * Carga los productos, roles y compañías para arreglo de permisos
   */
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setLoadingProductsAndRoles(true);
        const finalProductsList = [];
        const rolesMap = new Map();
        const tempCompaniesMap = new Map();

        //Traigo los productos activos con al menos un usuario disponible de la empresa
        const initialProductsList = await fetchProductsList(companyId, country);

        for (const product of initialProductsList) {
          //Formateo el producto
          const productFormatted = setProductFormat(product);

          //Traigo los roles por el idMenuOption del producto
          const initialRolesList = await fetchRolesByIdMenuOption(
            product.idMenuOption
          );
          //Formateo los roles al formato requerido por el render
          const finalRolesList = setRolesFormat(initialRolesList);

          //Traigo las empresas asociadas por el idMenuOption del producto
          const finalCompaniesList = await fetchCompaniesByIdMenuOption(
            product.idMenuOption
          );

          //Guardo el producto en la lista de productos
          finalProductsList.push(productFormatted);

          //Guardo la lista de roles en el mapa de roles
          rolesMap.set(product.idMenuOption, finalRolesList);

          //Guardo la lista de compañías en el mapa de compañias
          tempCompaniesMap.set(product.idMenuOption, finalCompaniesList);
        }

        //Setteo la lista de productos
        setProductsList(sortListByAttribute(finalProductsList, "label"));
        //Setteo mapa de roles
        setRolesMap(rolesMap);
        //Setteo mapa de compañías
        setCompaniesMap(tempCompaniesMap);
        setLoadingProductsAndRoles(false);

        // Selecciona automáticamente el 1er producto y la 1era empresa
        const productListValue = finalProductsList[0]?.value;
        setValue(`permissions.0.productList`, productListValue);
        const idMenuOption = productListValue
          ? JSON.parse(productListValue).idMenuOption
          : "";
        const companyList = tempCompaniesMap.get(idMenuOption) ?? [];
        if (companyList.length > 0)
          setValue(`permissions.0.companyList`, [companyList[0]]);
      } catch (error) {
        console.log("============ Error createUser.jsx ============");
        console.log(error);
        console.log("========================================");
      }
    };
    if (openCreateUser) {
      fetchPermissions();
    }
  }, [openCreateUser]);

  /**
   * Fetch
   */

  /**
   * Función trae los productos activos que tengan al menos un usuario disponible
   * @param {*} eanCompany ean de la empresa
   * @param {*} country país de la empresa
   * @returns lista de productos
   */
  const fetchProductsList = async (eanCompany, country) => {
    try {
      const {
        status,
        data: { responseCode, responseMessage },
      } = await GetActiveProduct({ eanCompany: eanCompany, country: country });

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST
      ) {
        return responseMessage;
      } else {
        return [];
      }
    } catch (error) {
      console.log("============ Error createUser.jsx ============");
      console.log(error);
      console.log("========================================");
      return [];
    }
  };

  /**
   * Función que trae la lista de roles según el idMenuOption
   * @param {*} idMenuOption opción de menú
   * @returns lista de roles
   */
  const fetchRolesByIdMenuOption = async (idMenuOption) => {
    try {
      const isClient = currentCompany.eanCompany !== "CASSANDRA" ? true : false;
      const {
        status,
        data: { responseCode, responseMessage },
      } = await GetRolesByProduct({
        idMenuOption: idMenuOption,
        isClient: isClient,
      });

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseCode === CODES.COD_RESPONSE_SUCCESS_REQUEST
      ) {
        return responseMessage;
      } else {
        return [];
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
      return [];
    }
  };

  /**
   * Función que trae la lista de empresas según el idMenuOption
   * @param {} idMenuOption opción de menú
   * @returns lista de empresas
   */
  const fetchCompaniesByIdMenuOption = async (idMenuOption) => {
    try {
      const obj = {
        country: currentCompany.country,
        eanCompany: currentCompany.eanCompany,
        idMenuOption,
      };

      const {
        status,
        data: { responseCode, responseMessage },
      } = await AvailableCompaniesPermissions(obj);

      if (status === CODES.COD_RESPONSE_HTTP_OK) {
        switch (responseCode) {
          case CODES.COD_RESPONSE_SUCCESS_REQUEST:
            setIsBusinessGroup(true);
            const list = responseMessage.map((item) => {
              return {
                nameCompany: `${item.nameCompany}`,
                renameCompany: `${item.country}-${item.nameCompany}`,
                country: item.country,
                eanCompany: item.eanCompany,
              };
            });

            return list;

          case CODES.COD_RESPONSE_ERROR_RESET_PASSWORD:
            setIsBusinessGroup(false);
            const currentCompanyInfo = {
              nameCompany: `${currentCompany.companyName}`,
              renameCompany: `${currentCompany.country}-${currentCompany.companyName}`,
              country: currentCompany.country,
              eanCompany: currentCompany.eanCompany,
            };

            return [currentCompanyInfo];

          default:
            //Setteo a true el businessGroup para permitir desplegar lista vacía y evidenciar que no
            //existen campos por seleccionar (error en la carga y evitar crash)
            setIsBusinessGroup(true);
            return [];
        }
      } else {
        return [];
      }
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
      return [];
    }
  };

  /**
   * Handles
   */

  /**
   * Función que abre el modal y agrega el primer permiso
   */
  const handleOpen = () => {
    reset();
    handleAddNewPermission();
    setOpenCreateUser(true);
  };

  /**
   * Función para cerrar el modal
   */
  const handleClose = async () => {
    await resetProductList();
    setOpenCreateUser(false);
    setLoadingCreate(false);
    reset();
  };

  /**
   * Método para actualizar el tamaño de pantalla actual
   */
  const updateMedia = () => {
    setDesktop(window.innerWidth > 490);
  };

  /**
   * Funcion para ordenar una lista por un atributo
   */
  const sortListByAttribute = (list, attribute) => {
    return [...list].sort((a, b) => {
      if (a[attribute] < b[attribute]) {
        return -1;
      }
      if (a[attribute] > b[attribute]) {
        return 1;
      }
      return 0;
    });
  };

  /**
   * Función que formatea la lista de roles a el formato requerido para el render
   * @param {} rolesList lista de roles
   * @returns lista de roles formateada
   */
  const setRolesFormat = (rolesList) => {
    return rolesList.map((role) => ({
      label: role.nameRole,
      value: `{"idRole": "${role.idRole}", "nameRole": "${role.nameRole}"}`,
    }));
  };

  /**
   * Función que formatea el producto a el formato requerido para el render
   * @param {*} product producto
   * @returns producto formateado
   */
  const setProductFormat = (product) => {
    return {
      label: product.menuOptionName,
      value: `{ "idMenuOption": "${product.idMenuOption}", "menuOptionName": "${product.menuOptionName}" }`,
      isActive: true,
    };
  };

  /**
   * Función para generar un id genérico para renderizar lista de permisos
   * @returns id genérico
   */
  const generateId = () => {
    const id = Date.now().toString(32) + Math.random().toString(32);

    return id;
  };

  /**
   * Función para añadir un permiso
   */
  const handleAddNewPermission = () => {
    const userInfoCompany = {
      nameCompany: `${currentCompany.companyName}`,
      renameCompany: `${currentCompany.country}-${currentCompany.companyName}`,
      country: currentCompany.country,
      eanCompany: currentCompany.eanCompany,
    };
    const newPermission = {
      productSelection: false,
      companyList: isBusinessGroup ? [] : [userInfoCompany],
      key: generateId(),
    };

    permissionsAppend(newPermission);
  };

  /**
   *
   * Función para remover un permiso del arreglo de permisos
   *
   * @param {String} id
   * @param {Number} index
   */
  const handleRemovePermission = (index) => {
    const previousValue = getValues(`permissions.${index}.productList`);
    const convertedPrevious = previousValue
      ? JSON.parse(previousValue)
      : previousValue;

    const updatedProductList = productsList.map((item) => {
      const product = JSON.parse(item.value);
      const convertedCurrent = product.idMenuOption;

      if (convertedCurrent === convertedPrevious.idMenuOption) {
        return { ...item, selected: false };
      }

      return item;
    });

    setProductsList(sortListByAttribute(updatedProductList, "label"));

    permissionsRemove(index);
  };

  /**
   *
   * Función para actualizar el estado de productsList cuando el usuario hace click en el campo producto
   * @param {Event} event
   * @param {*} previous
   */
  const handleProductChange = (event, previous, index) => {
    const selectedProduct = event.target.value;
    const selectedConverted = JSON.parse(selectedProduct);
    const previousValue = previous;
    const previousConverted = previousValue ? JSON.parse(previousValue) : "";

    //Reseteo los campos de compañias, rol y estado

    if (isBusinessGroup) {
      setValue(`permissions.${index}.companyList`, []);
    }
    setValue(`permissions.${index}.roleList`, []);
    setValue(`permissions.${index}.status`, "");

    // Esto permite que se actualice el array y se habiliten los campos
    // de estado y rol
    const permission = getValues("permissions.0");
    permissionsUpdate(0, permission);

    const updatedProductList = productsList.map((item) => {
      const product = JSON.parse(item.value);
      const convertedValue = product.idMenuOption;

      if (convertedValue === selectedConverted.idMenuOption) {
        return { ...item, selected: true };
      } else if (convertedValue === previousConverted.idMenuOption) {
        return { ...item, selected: false };
      }

      return item;
    });

    setProductsList(sortListByAttribute(updatedProductList, "label"));
  };

  /**
   * Función que resetea la selección de la lista de productos
   */
  const resetProductList = () => {
    const resetList = productsList.map((item) => {
      return { ...item, selected: false };
    });

    setProductsList(sortListByAttribute(resetList, "label"));
  };

  /**
   *
   * Función que construye el objeto de usuario
   *
   * @param {Object} data información del usuario
   * @returns objeto de usuario según formato
   */
  const handlCreateUserObject = (data) => {
    //Construyo el objeto de usuario
    const userObject = {
      eanCompany: currentCompany.eanCompany,
      companyName: currentCompany.companyName,
      firstName: data.name,
      lastName: data.lastNames,
      email: data.email,
      phone: data.phone,
      nrps: data.nrps,
    };

    //Tomo el arreglo de permisos y lo construyo conforme cuerpos para el back
    const permissionsModification = data.permissions.map((permission) => {
      const parsedProduct = JSON.parse(permission.productList);
      const parsedRole = JSON.parse(permission.roleList);

      const companies = permission.companyList.map((company) => company);

      return {
        ...parsedProduct,
        ...parsedRole,
        lstCompanies: companies,
        state: Number(permission.status),
      };
    });

    //Construimos el arreglo final
    const userPermissionsArray = permissionsModification.map((permission) => {
      return { ...userObject, ...permission };
    });
    return userPermissionsArray;
  };

  /**
   * Función que retorna el título del modal según código de respuesta
   * @param {Number} responseCode código de respuesta
   * @returns título según código
   */
  const handleModalTitle = (responseCode) => {
    if (responseCode === 1001) {
      return t("App.userDetail.modalTitle.title2");
    } else if (responseCode === 1002) {
      return t("App.userDetail.modalTitle.title1");
    } else {
      return "";
    }
  };

  /**
   *
   * Function to create a new user
   *
   * @param {Object} data
   */
  const handleCreateUser = async (data) => {
    try {
      const userCreateArray = handlCreateUserObject(data);

      setLoadingCreate(true);

      const obj = {
        userCreateArray,
        country: currentCompany.country,
        userName: userInfo.firstName + " " + userInfo.lastName,
        mainUser: userInfo?.mainUser,
      };

      const createUserService = await CreateUsers(obj);

      if (
        createUserService.status === CODES.COD_RESPONSE_HTTP_OK &&
        createUserService.data.responseCode ===
          CODES.COD_RESPONSE_SUCCESS_REQUEST
      ) {
        fetchUsers();
      }

      const responseCode = createUserService.data.responseCode;

      setModalTitle(handleModalTitle(responseCode));
      setOpenModalInfo(true);
      setCreateUserData(createUserService);

      setLoadingCreate(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  return (
    <>
      <Button
        startIcon={<AddIcon sx={{ fontSize: 22 }} />}
        className="btn__filled__blue btn__applyFilter"
        onClick={handleOpen}
      >
        {t("App.adminClientUsers.buttonCreateUsuario")}
      </Button>

      <Dialog
        onClose={handleClose}
        open={openCreateUser}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              height: "100%",
              maxWidth: "900px",
              maxHeight: "575px",
              boxShadow: "none",
              borderRadius: "22px",
              padding: "2rem",
              overflow: "hidden",
            },
          },
        }}
      >
        <DialogTitleContainer onClose={handleClose}>
          <Grid container direction="column" rowSpacing={1}>
            <Grid item className="display-large__primary-one">
              {t("App.createUser.title")}
            </Grid>
          </Grid>
        </DialogTitleContainer>

        <DialogContent className="scroll-table">
          <form id="form-permissions" onSubmit={handleSubmit(handleCreateUser)}>
            <Grid container item direction="column" spacing={1}>
              <Grid item style={{ width: "90%" }}>
                <Grid
                  container
                  direction="row"
                  justifyContent={"flex-start"}
                  alignItems="center"
                  className="modalCreateUsers__form-paragraph"
                >
                  <Grid item style={{ padding: "0.5%" }} className="col-flex">
                    <InfoOutlinedIcon fontSize="large" />
                  </Grid>

                  <Grid item className="col-flex">
                    <p>{t("App.createUser.paragraph")}</p>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <h3 className="heading__text-grey">
                  {t("App.createUser.information")}
                </h3>
              </Grid>
              <Grid item paddingBottom={errors.email ? "2rem" : ""}>
                <UserInfoComponent control={control} errors={errors} t={t} />
              </Grid>
              <br />
              <Grid item paddingBottom="0.7rem">
                <h3 className="heading__text-grey">
                  {t("App.createUser.permit")}
                </h3>
              </Grid>
              <br />
              <Grid item container className="scroll-table permissionBox">
                <PermissionsRegisters
                  permissionsFields={permissionsFields}
                  handleProductChange={handleProductChange}
                  control={control}
                  getValues={getValues}
                  setValue={setValue}
                  productsList={productsList}
                  errors={errors}
                  loadingProductsAndRoles={loadingProductsAndRoles}
                  isBusinessGroup={isBusinessGroup}
                  rolesMap={rolesMap}
                  companiesMap={companiesMap}
                />
              </Grid>

              <br />
              <Grid item container className="permissionBox">
                <UserNRPList control={control} setValue={setValue} />
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        <DialogActions>
          <Grid
            container
            direction={isDesktop ? "row" : "column-reverse"}
            className="col-end"
            columnGap={4}
            sx={{ padding: "1.5%" }}
          >
            <Grid item xs={12} sm={2} lg={1} className="full-width">
              <Button
                className="btn__filled__gradient full-width"
                onClick={handleClose}
              >
                {t("App.buttonLabels.cancel")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} lg={2.2} className="full-width">
              <Button
                className="btn__applyFilter"
                type="submit"
                form="form-permissions"
                startIcon={!loadingCreate ? <AddIcon /> : <></>}
              >
                {!loadingCreate ? (
                  t("App.createUser.title")
                ) : (
                  <CircularProgress size={21} color="inherit" />
                )}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <ModalInfo
        title={modalTitle}
        responseData={createUserData}
        onClose={() => {
          handleClose();
          setOpenModalInfo(false);
        }}
        open={openModalInfo}
      />
    </>
  );
};

export default CreateUser;
